import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { SellerPriceDetails } from '@usf/price-types';
import { Observable } from 'rxjs';
import { HandPricingDetails } from '../../../order/models/order-view-model';
import { PlatformService } from '@panamax/app-state';
import { allowOnlyNumbers } from '@product-detail/utils/cpr.util';

@Component({
  selector: 'app-hand-pricing-popover',
  templateUrl: './hand-pricing-popover.component.html',
  styleUrls: ['./hand-pricing-popover.component.scss'],
})
export class HandPricingPopoverComponent implements OnInit {
  @Input() productDetails: HandPricingDetails;
  @Input() altProductDetails: HandPricingDetails;
  @Input() sellerPrice: any;
  @Input() altSellerPrice: any;
  @Input() currentPriceOverride: number;
  @Input() errorMessage?: string;
  sellerPriceDetails$: Observable<SellerPriceDetails>;
  sellerPriceDetails: SellerPriceDetails;
  prices = undefined;
  selectedPriceType = new UntypedFormControl('custom');
  customPrice = new UntypedFormControl(0);
  currentTab = 1;
  expandAdditionalPricing: boolean;
  isSubstitute = false;

  constructor(
    private modalController: ModalController,
    readonly platformService: PlatformService,
  ) {}

  dismiss() {
    this.modalController.dismiss();
  }

  ngOnInit(): void {
    this.isSubstitute = !this.productDetails?.description;
    this.prices = !!this.productDetails?.description
      ? this.mapPricingDetails(this.sellerPrice, this.productDetails)
      : this.mapPricingDetails(this.altSellerPrice, this.altProductDetails);
    this.setCustomPrice();
  }

  mapPricingDetails(pricingDetails, productDetails: HandPricingDetails) {
    const currentPrice = pricingDetails?.currentPrice;
    const cookbookPrices = currentPrice?.cookbookPrices;
    const divisionCosts = currentPrice?.divisionCosts;
    const autoShippingCharges = currentPrice?.autoShippingCharges;

    const cookbookMaximumPrice = cookbookPrices?.cookbookMaximumPrice;
    const listAmount = divisionCosts?.listAmount;
    const targetPrice = cookbookPrices?.cookbookTargetPrice;
    const sellAmount = divisionCosts?.sellAmount;

    let maximum = cookbookMaximumPrice > 0 ? cookbookMaximumPrice : listAmount;
    let minimum = divisionCosts?.commissionAmount;
    let target = targetPrice > 0 ? targetPrice : sellAmount;

    if (pricingDetails?.customerAscInd === 'P') {
      const unitAscRate = autoShippingCharges?.unitAscRate || 0;
      maximum = maximum + unitAscRate;
      minimum = minimum + unitAscRate;
      target = target + unitAscRate;
    }

    const additionalPricingInfo = {
      unitPrice: currentPrice?.unitPrice,
      eachPrice: currentPrice?.eachPrice,
      commissionAmount: divisionCosts?.commissionAmount,
      ascInd: pricingDetails?.customerAscInd,
      unitAscRate: autoShippingCharges?.unitAscRate,
      eachAscRate: autoShippingCharges?.eachAscRate,
      grossProfitPercent: currentPrice?.grossProfitPercent,
      grossProfitDollars: currentPrice?.grossProfitDollars,
    };

    return (pricingDetails = {
      maximum,
      minimum,
      target,
      additionalPricingInfo,
      ...productDetails,
    });
  }

  updatePrice() {
    const newPrice = this.validateHandPrice(this.getSelectedPriceValue());
    this.modalController.dismiss({
      productNumber: this.prices.productNumber,
      unitPrice: newPrice,
      unitPriceOverride: this.calculatePriceWithASC(newPrice),
    });
  }

  getSelectedPriceValue = () => {
    let selectedPrice = this.customPrice.value;
    if (this.selectedPriceType.value !== 'custom') {
      selectedPrice =
        this.prices[this.selectedPriceType.value] || this.prices.currentPrice;
    }
    return selectedPrice;
  };

  calculatePriceWithASC = price => {
    const sellerPrice = this.isSubstitute
      ? this.altSellerPrice
      : this.sellerPrice;
    const ascCharge =
      sellerPrice?.currentPrice?.autoShippingCharges?.unitAscRate || 0;
    return sellerPrice?.customerAscInd === 'P' ? price - ascCharge : price;
  };

  validateHandPrice(price) {
    const sellerPrice = this.isSubstitute
      ? this.altSellerPrice
      : this.sellerPrice;

    if (sellerPrice?.customerAscInd === 'P') {
      const ascCharge =
        sellerPrice.currentPrice.autoShippingCharges.unitAscRate;
      return price - ascCharge < 0 ? ascCharge : price;
    } else {
      return price < 0 ? 0 : price;
    }
  }

  toggleTab(tab: number) {
    if (tab === 2) {
      this.prices = this.mapPricingDetails(
        this.altSellerPrice,
        this.altProductDetails,
      );
    } else {
      this.prices = this.mapPricingDetails(
        this.sellerPrice,
        this.productDetails,
      );
    }
    if (this.currentTab !== tab) {
      this.currentTab = tab !== 2 ? 1 : 2;
      this.setCustomPrice();
      this.isSubstitute = !this.isSubstitute;
    }
  }

  setCustomPrice() {
    const selectedPrice = parseFloat(
      (this.prices?.newUnitPrice
        ? this.prices.newUnitPrice +
          this.prices.additionalPricingInfo?.unitAscRate
        : this.prices?.currentPrice) || '0',
    ).toFixed(2);
    this.customPrice.setValue(selectedPrice);
  }

  allowOnlyNumbers(event) {
    return allowOnlyNumbers(event);
  }
}
